import tenantUtils from '@utils';
// Plain antd primitives rather than the shared styled wrappers: the design's card and tag carry
// none of the shared chrome (clamped card radius, tenant border widths, round-tag padding), only
// the geometry set inline below.
import { Card, Divider, Tag } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { formatCompletionQuarter } from '../../../../helpers/post-listing';
import { getBaseURL } from '../../../../utility/env';

const PLACEHOLDER_IMAGE = `${getBaseURL()}/profolio-assets/images/ph-listings.svg`;

const EYEBROW_STYLE = { lineHeight: 1.4, textTransform: 'uppercase', letterSpacing: '.06em' };
const FIELD_GRID_STYLE = { display: 'grid', gridAutoColumns: 'max-content', gap: 40 };

/**
 * Shown in place of a value the backend did not send, so the card keeps a stable set of labels
 * instead of reflowing as coverage varies (engineer decision — the design omits empty fields).
 */
const EMPTY_VALUE = '-';

const Field = ({ label, value }) => (
  <div>
    <div className="text-muted fz-12" style={{ lineHeight: 1.4, marginBlockEnd: 2 }}>
      {label}
    </div>
    <div className="fw-700 fz-14" style={{ lineHeight: 1.4, color: '#222', whiteSpace: 'nowrap' }}>
      {value == null || value === '' ? EMPTY_VALUE : value}
    </div>
  </div>
);

/**
 * Read-only summary of the project (and unit type) a listing was spawned from, shown above the
 * Edit form for Bayut KSA project unit-type listings in place of the REGA Details card — these
 * listings have no REGA path at all, so `RegaCard` self-hides for them.
 *
 * Every labelled field always renders: among KSA projects with live listings only ~93% carry a unit
 * count, ~80% a WAFI licence and ~80% a completion date, and `Units in this Unit Type` is not sent
 * at all today, so a value the backend omits shows as `-` rather than dropping the label.
 */
const ProjectDetailCard = ({ projectDetailCard, fallbackWafiLicense }) => {
  const { t } = useTranslation();
  const isMobile = useSelector((state) => state.app.AppConfig.isMobile);

  const project = projectDetailCard?.project;
  const unitType = projectDetailCard?.unit_type;
  if (!project) return null;

  const projectName = tenantUtils.getLocalisedString(project, 'name');
  const unitTypeName = tenantUtils.getLocalisedString(unitType, 'name');
  // BE sends the unit-type count as `total_units`; `0` is a real value and renders as `0`, not `-`.
  const unitTypeUnits = unitType?.total_units ?? unitType?.units_count ?? null;
  // Same resolution chain as the My Listings row (bayut/transformers/listings.js): prefer the
  // licence on the project card, then the one at the payload root. The edit response carries
  // `wafi_license` at the root and omits it from `project_detail_card.project` entirely, so
  // reading only the nested key left the field empty.
  const wafiLicense = project?.wafi_license ?? fallbackWafiLicense ?? null;

  const projectFields = [
    { label: t('WAFI Licence'), value: wafiLicense },
    { label: t('Project Completion Date'), value: formatCompletionQuarter(project?.completion_date) },
    { label: t('Units in Project'), value: project?.total_units },
  ];

  const unitTypeFields = [
    { label: t('Unit Type'), value: unitTypeName },
    { label: t('Units in this Unit Type'), value: unitTypeUnits },
  ];

  const renderFields = (fields, marginBlockStart) => (
    <div
      style={{
        ...FIELD_GRID_STYLE,
        // Desktop lays the fields out in a single row; a phone viewport cannot fit that plus the
        // 32px-inset vertical divider, so both stack instead.
        gridAutoFlow: isMobile ? 'row' : 'column',
        gap: isMobile ? 12 : FIELD_GRID_STYLE.gap,
        marginBlockStart: isMobile ? 12 : marginBlockStart,
      }}
    >
      {fields.map((field) => (
        <Field key={field.label} label={field.label} value={field.value} />
      ))}
    </div>
  );

  return (
    <Card bordered style={{ borderRadius: 8, marginBlockEnd: 16 }} styles={{ body: { padding: 16 } }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 16,
          ...(isMobile && { flexDirection: 'column' }),
        }}
      >
        <div style={{ width: 96, height: 96, borderRadius: 8, overflow: 'hidden', flex: '0 0 auto' }}>
          <img
            draggable={false}
            src={project?.image || PLACEHOLDER_IMAGE}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        </div>

        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
          <div className="fw-700 text-muted fz-12" style={EYEBROW_STYLE}>
            {t('Project Details')}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBlock: '2px 0' }}>
            <div className="fw-700" style={{ fontSize: 18, lineHeight: 1.3, color: '#222' }}>
              {projectName || EMPTY_VALUE}
            </div>
            {!!project?.status && (
              <Tag
                className="fz-12"
                color="#e4f3fc"
                bordered={false}
                style={{
                  '--tag-color': '#0793ea',
                  color: '#0793ea',
                  fontWeight: 700,
                  borderRadius: 999,
                  margin: 0,
                  lineHeight: 1.6,
                }}
              >
                {project.status === 'ready' ? t('Ready') : t('Off-plan')}
              </Tag>
            )}
          </div>
          {/* 12px below the eyebrow; the UNIT TYPE group uses 37px so both field rows share a
              baseline despite this group carrying the extra name + tag line. */}
          {renderFields(projectFields, 12)}
        </div>

        <Divider
          type={isMobile ? 'horizontal' : 'vertical'}
          style={
            isMobile ? { marginBlock: 4 } : { height: 'auto', alignSelf: 'stretch', marginInline: 32, top: 0 }
          }
        />

        <div style={{ flex: '0 0 auto' }}>
          <div className="fw-700 text-muted fz-12" style={EYEBROW_STYLE}>
            {t('Unit Type')}
          </div>
          {renderFields(unitTypeFields, 37)}
        </div>
      </div>
    </Card>
  );
};

export default ProjectDetailCard;
